import { Paper, ScrollArea, Spoiler, Text } from '@mantine/core'
import { modals } from '@mantine/modals'
import withErrorBoundaryAndPolicyCheck from '@v2/commons/HOC/ErrorBoundaryAndPolicyCheckHOC'
import ActionMenu from '@v2/commons/components/ActionMenu/ActionMenu.component'
import showModalNotification from '@v2/commons/modals/ModalNotification/showModalNotification'
import { EnumModalIcons, EnumModalType } from '@v2/commons/modals/types'
import { WbfPolicies } from '@workers-benefit-fund/shared-data'
import dayjs from 'dayjs'
import { useEffect, useRef, useState } from 'react'

import { Icons } from 'src/common/components'

import { IMessage, UpdateMessageMutation } from '../../types/chat.types'
import EditNote from '../AddNewMessage/addMessage.component'
import styles from './chat-message-list.module.scss'

interface ChatMessageListProps {
  messages: IMessage[]
  onUpdateNote: (content: string, chatMessageId: string) => void
  updateChatMessageMutationStatus: { isSuccess: boolean; isLoading: boolean }
  onDeleteNote: (chatMessageId: string) => void
  deleteChatMessageMutationStatus: { isSuccess: boolean; isLoading: boolean }
  hasDeleteMessagePolicy: boolean
  hasUpdtaeMessagePolicy: boolean
  currentUserId: string
}

const ChatList = ({
  messages,
  onUpdateNote,
  updateChatMessageMutationStatus,
  onDeleteNote,
  hasUpdtaeMessagePolicy,
  hasDeleteMessagePolicy,
  currentUserId,
}: ChatMessageListProps) => {
  const [editmode, setEditMode] = useState(false)
  const [editMessage, setEditMessage] = useState<UpdateMessageMutation | null>(
    null,
  )
  const delteNoteModal = useRef<string>('')

  useEffect(() => {
    if (updateChatMessageMutationStatus.isSuccess) {
      setEditMode(false)
    }
  }, [updateChatMessageMutationStatus.isSuccess])

  const closeDeleteNoteModal = () => {
    if (delteNoteModal.current) {
      modals.close(delteNoteModal.current)
      delteNoteModal.current = ''
    }
  }

  const getFormattedDate = (date: string) => {
    return dayjs.utc(date).local().format('MMM D, YYYY, hh:mm A')
  }

  const onUpdate = (message: UpdateMessageMutation) => {
    setEditMode(true)
    setEditMessage(message)
  }

  const showActionMenu = hasUpdtaeMessagePolicy || hasDeleteMessagePolicy

  const isCurrentUserMessageAutor = (autorId: string) => {
    return autorId === currentUserId
  }

  const onDelete = (chatMessageId: string) => {
    delteNoteModal.current = showModalNotification({
      Icon: EnumModalIcons.Trash,
      title: 'Delete Note',
      subtitle:
        'Are you sure you want to permanently delete the note? This action cannot be undone.',
      buttonText: 'Delete',
      titleColor: 'var(--mantine-color-red-6)',
      type: EnumModalType.DELETE,
      onClickBtn: () => onDeleteNote(chatMessageId),
      closeOnSuccess: closeDeleteNoteModal,
    })
  }

  const handleUpdateNote = (content: string) => {
    onUpdateNote(content, editMessage!.chatMessageId)
  }

  return (
    <>
      {editmode ? (
        <EditNote
          onCancel={() => setEditMode(false)}
          onAddNote={handleUpdateNote}
          isLoading={updateChatMessageMutationStatus.isLoading}
          content={editMessage?.message ?? ''}
        />
      ) : (
        <ScrollArea.Autosize
          type="scroll"
          scrollbars="y"
          mah={341}
          mt={32}
          offsetScrollbars={false}
        >
          {messages.map(({ createdAt, message, user, id }, idx) => (
            <Paper
              key={id + idx}
              classNames={{
                root: styles['chat-message-list-item'],
              }}
            >
              {showActionMenu && isCurrentUserMessageAutor(user?.id ?? '') && (
                <Paper style={{ position: 'absolute', top: 0, right: 9 }}>
                  <ActionMenu
                    ButtonIcon={<Icons.HorizontalDotsMenu />}
                    menuPosition="bottom"
                    menuItems={[
                      {
                        label: 'Edit',
                        color: '#666666',
                        icon: <Icons.Edit />,
                        onClick: () => onUpdate({ message, chatMessageId: id }),
                        enable: hasUpdtaeMessagePolicy,
                      },
                      {
                        label: 'Delete',
                        color: 'var(--mantine-color-red-6)',
                        icon: <Icons.Trash />,
                        onClick: () => onDelete(id),
                        enable: hasDeleteMessagePolicy,
                      },
                    ]}
                  />
                </Paper>
              )}
              <Text className={styles['created-label']}>
                {getFormattedDate(createdAt)}
              </Text>
              <Spoiler
                maxHeight={90}
                pt={24}
                pr={24}
                pl={24}
                showLabel={<Icons.ArrowExpand />}
                hideLabel={
                  <Icons.ArrowExpand
                    className={styles['arrow-expand-icon']}
                    style={{ transform: 'rotate(180deg)' }}
                  />
                }
                key={id}
                classNames={{
                  root: styles['toggle-show-root'],
                  control: styles['toggle-show-control'],
                  content: styles['toggle-show-content'],
                }}
              >
                <Text fz={16} fw={700} c={'#21252C'}>
                  {user?.firstName + '' + user?.lastName}
                </Text>
                <Text
                  w={'100%'}
                  component="div"
                  className={styles['text-content']}
                  dangerouslySetInnerHTML={{
                    __html: message,
                  }}
                  mb={16}
                />
              </Spoiler>
            </Paper>
          ))}
        </ScrollArea.Autosize>
      )}
    </>
  )
}
export default withErrorBoundaryAndPolicyCheck(
  ChatList,
  WbfPolicies.is_get_chat_messages_enabled,
)
